import React, { useState, useEffect } from 'react';
import corporateService from './corporates/corporateService';

const CorporatePage = () => {
    // State for posts list
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // State for new post form
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        location: '',
        description: '',
        condition: 'new',
        image: null
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [preview, setPreview] = useState(null);

    // Fetch posts on component mount
    useEffect(() => {
        fetchPosts();
    }, []);

    // Function to fetch all posts
    const fetchPosts = async () => {
        setLoading(true);
        try {
            const data = await corporateService.getAllPosts();
            setPosts(data);
            setError(null);
        } catch (err) {
            setError('Failed to load posts. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    // Handle image input changes
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({
                ...formData,
                image: file
            });

            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setPreview(previewUrl);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError(null);

        try {
            await corporateService.createPost(formData);
            // Reset form
            setFormData({
                title: '',
                location: '',
                description: '',
                condition: 'new',
                image: null
            });
            setPreview(null);
            setShowForm(false);
            // Refresh posts
            fetchPosts();
        } catch (err) {
            setSubmitError('Failed to create post. Please try again.');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle post deletion
    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this post?')) {
            try {
                await corporateService.deletePost(id);
                // Refresh posts
                fetchPosts();
            } catch (err) {
                console.error('Failed to delete post:', err);
                alert('Failed to delete post. Please try again.');
            }
        }
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold text-[#001f3f]">Corporate Social Posts</h1>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="px-4 py-2 bg-[#001f3f] text-white rounded-md hover:bg-[#00152b] transition duration-300"
                >
                    {showForm ? 'Cancel' : 'Add New Post'}
                </button>
            </div>

            {/* Form for adding new posts */}
            {showForm && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h2 className="text-xl font-semibold mb-4">Create New Post</h2>

                    {submitError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
                            <span>{submitError}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2" htmlFor="title">
                                    Title
                                </label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001f3f]"
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 font-medium mb-2" htmlFor="location">
                                    Location
                                </label>
                                <input
                                    type="text"
                                    id="location"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001f3f]"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="description">
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                rows="4"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001f3f]"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="condition">
                                Condition
                            </label>
                            <select
                                id="condition"
                                name="condition"
                                value={formData.condition}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001f3f]"
                            >
                                <option value="new">New</option>
                                <option value="used">Used</option>
                                <option value="refurbished">Refurbished</option>
                                <option value="damaged">Damaged</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-gray-700 font-medium mb-2" htmlFor="image">
                                Image
                            </label>
                            <input
                                type="file"
                                id="image"
                                name="image"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001f3f]"
                            />

                            {preview && (
                                <div className="mt-2">
                                    <p className="text-sm text-gray-600 mb-1">Preview:</p>
                                    <img src={preview} alt="Preview" className="w-48 h-48 object-cover rounded-md" />
                                </div>
                            )}
                        </div>

                        <div className="mt-6">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-6 py-2 bg-[#001f3f] text-white rounded-md hover:bg-[#00152b] transition duration-300 disabled:bg-gray-400"
                            >
                                {submitting ? 'Submitting...' : 'Create Post'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Loading state */}
            {loading && (
                <div className="flex justify-center items-center my-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#001f3f]"></div>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
                    <strong className="font-bold">Error! </strong>
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {/* Posts grid */}
            {!loading && !error && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {posts.length === 0 ? (
                        <div className="col-span-full text-center py-12">
                            <p className="text-gray-600 text-lg">No posts found. Create a new post to get started!</p>
                        </div>
                    ) : (
                        posts.map((post) => (
                            <div key={post.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                                {post.image ? (
                                    <div className="h-48 overflow-hidden">
                                        <img
                                            src={post.image}
                                            alt={post.title}
                                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                                        />
                                    </div>
                                ) : (
                                    <div className="h-48 bg-gray-200 flex items-center justify-center">
                                        <span className="text-gray-500">No image</span>
                                    </div>
                                )}

                                <div className="p-4">
                                    <div className="flex justify-between items-start">
                                        <h3 className="text-xl font-semibold text-gray-800 mb-2">{post.title}</h3>
                                        <span className={`px-2 py-1 text-xs rounded-full ${post.condition === 'new' ? 'bg-[#e6f0ff] text-[#001f3f]' :
                                            post.condition === 'used' ? 'bg-[#cce0ff] text-[#001f3f]' :
                                                post.condition === 'refurbished' ? 'bg-[#b3d1ff] text-[#001f3f]' :
                                                    'bg-[#99c2ff] text-[#001f3f]'
                                            }`}>
                                            {post.condition.charAt(0).toUpperCase() + post.condition.slice(1)}
                                        </span>
                                    </div>

                                    <p className="text-gray-600 mb-2">
                                        <span className="inline-block mr-1">📍</span> {post.location}
                                    </p>

                                    <p className="text-gray-700 mb-4">
                                        {post.description.length > 100
                                            ? `${post.description.substring(0, 100)}...`
                                            : post.description}
                                    </p>

                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-500">
                                            {new Date(post.created_at).toLocaleDateString()}
                                        </span>
                                        <button
                                            onClick={() => handleDelete(post.id)}
                                            className="text-[#001f3f] hover:text-[#00152b] transition duration-300"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default CorporatePage;